import React, { useCallback, useEffect, useRef, useState } from 'react';
import InfoTooltip from './InfoTooltip';
import { supabase, isSupabaseEnabled, shortCitizen } from './lib/supabaseClient';
import { IdentityLevel, IdentityTier } from './lib/identity';
import { listMockLeaders, subscribeMockLeaderboard } from './lib/mockLeaderboard';
import { calcDivergencePoints } from './lib/divergence';

// Unified podium row. Mock rows carry `alias`, `tier`, `rp`, `accuracy`
// from the seed; Supabase-derived rows (real pledges from the currentUser
// and anyone else who's staked in this sandbox) carry a `null` tier and
// synthesized RP/accuracy so the podium reads uniformly.
interface DisplayRow {
  user_id:      string;
  alias:        string | null;     // mock rows have an alias; real rows fall back to shortCitizen
  tier:         IdentityTier | null;
  units_total:  number;
  cost_total:   number;
  dp:           number;             // Σ units × (1 − price_at_pledge)
  rp:           number;             // Reputation Points
  accuracy:     number;             // Win rate over last 20 signals, 0..100
  pledge_count: number;
  isMock:       boolean;
}

const MEDALS = ['🥇', '🥈', '🥉'];
const PALETTE = ['#7dd3fc', '#86efac', '#fcd34d', '#c084fc', '#fb923c', '#f87171', '#60a5fa', '#a3e635'];

// Deterministic accent per citizen id so the same Ghost / mock alias
// gets the same colour across renders without a central registry.
function accentFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
}

// Sandbox-only RP / accuracy synthesis for real Supabase rows. The
// production reputation engine will replace this. The shape matches the
// intuition from §2: RP trails DP at ~18 %, accuracy centres on 50 %
// with a slight lift for high-per-pledge contrarians. Capped [35, 65]
// so a brand-new citizen with a lucky first pledge doesn't leapfrog
// Sovereigns on the accuracy axis.
function syntheticRP(dp: number): number {
  return Math.round(dp * 0.18);
}
function syntheticAccuracy(pledgeCount: number, dp: number): number {
  if (pledgeCount === 0) return 50;
  const avgDpPerPledge = dp / pledgeCount;
  return Math.max(35, Math.min(65, 50 + (avgDpPerPledge - 60) * 0.05));
}

interface Props { currentUser: string; onOpenManifesto?: () => void; }

export default function Leaderboard({ currentUser, onOpenManifesto }: Props): React.JSX.Element {
  const [supaRows, setSupaRows] = useState<DisplayRow[]>([]);
  const [, setMockTick]         = useState(0);
  const cancelled = useRef(false);

  const loadBoard = useCallback(async (): Promise<void> => {
    // Hard-off: only wipe rows when Supabase is explicitly disabled. A
    // transient fetch hiccup should NOT collapse the board — doing so
    // shrinks the drawer and cascades into the DP explainer below,
    // which is the origin of the "stone rolling" layout shift.
    if (!isSupabaseEnabled() || !supabase) { if (!cancelled.current) setSupaRows([]); return; }
    const { data, error } = await supabase
      .from('pledges')
      .select('user_id, units, price_at_pledge, total_cost');
    // On transient errors or an undefined payload, keep the last known
    // rows on screen. The realtime subscription + 30s poll will refill
    // soon enough; what we must NOT do is flash an empty podium.
    if (error || !data) return;

    // Aggregate by user_id. DP is the canonical divergence compute:
    //   dp += units × (1 − price_at_pledge)
    // The factor (1 − price_at_pledge) rewards pledges made when the
    // consensus rated the outcome unlikely, which is the signal the
    // Divergence Leaderboard surfaces.
    const byUser = new Map<string, DisplayRow>();
    for (const p of data as Array<{ user_id: string; units: number | string; price_at_pledge: number | string; total_cost: number | string }>) {
      const u     = p.user_id;
      const units = Number(p.units)           || 0;
      const price = Number(p.price_at_pledge) || 0;
      const cost  = Number(p.total_cost)      || 0;
      const prev  = byUser.get(u) ?? {
        user_id:      u,
        alias:        null,
        tier:         null,
        units_total:  0,
        cost_total:   0,
        dp:           0,
        rp:           0,
        accuracy:     0,
        pledge_count: 0,
        isMock:       false,
      };
      prev.units_total  += units;
      prev.cost_total   += cost;
      prev.dp           += calcDivergencePoints(units, price);
      prev.pledge_count += 1;
      byUser.set(u, prev);
    }
    // Backfill synthesized RP / accuracy so the combined podium has
    // full columns across every row.
    const rows = Array.from(byUser.values()).map(r => ({
      ...r,
      rp:       syntheticRP(r.dp),
      accuracy: syntheticAccuracy(r.pledge_count, r.dp),
    }));
    if (!cancelled.current) setSupaRows(rows);
  }, []);

  useEffect(() => {
    cancelled.current = false;
    loadBoard();
    // Long-interval poll as a fallback; Realtime is the primary signal.
    const poll = setInterval(loadBoard, 30_000);

    const channel = supabase
      ?.channel('agoracle:pledges:leaderboard')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'pledges' },
        () => { loadBoard(); },
      )
      .subscribe();

    // Subscribe to the mock field so the podium glides when the churn
    // ticker bumps a row. State bump is all we need — listMockLeaders
    // is read fresh on every render.
    const unsubMock = subscribeMockLeaderboard(() => setMockTick(t => t + 1));

    return () => {
      cancelled.current = true;
      clearInterval(poll);
      unsubMock();
      if (channel && supabase) supabase.removeChannel(channel);
    };
  }, [loadBoard]);

  // Merge mock rows (always present) with any Supabase-derived rows.
  // A real user whose user_id happens to collide with a mock id would
  // be shadowed by the mock — but mock ids are prefixed `mock_` and
  // real ids are Ghost UUIDs, so the namespaces are disjoint by design.
  const mockRows: DisplayRow[] = listMockLeaders().map(m => ({
    user_id:      m.user_id,
    alias:        m.alias,
    tier:         m.tier,
    units_total:  m.unitsTotal,
    cost_total:   m.costTotal,
    dp:           m.dp,
    rp:           m.rp,
    accuracy:     m.accuracy,
    pledge_count: m.pledgeCount,
    isMock:       true,
  }));
  const rows = [...mockRows, ...supaRows].sort((a, b) => b.dp - a.dp);
  const maxDp = Math.max(...rows.map(r => r.dp), 1);

  return (
    <div style={{
      backgroundColor: '#0f172a',
      border:          '1px solid #333',
      borderRadius:    8,
      padding:         '10px 12px',
      fontFamily:      'monospace',
      // Cockpit-tight: a shorter reserved height so the Alpha footer
      // row never gets pushed off-screen. Rows scroll inside the
      // `.terminal-scroll` wrapper the panel sits in, so the minimum
      // only needs to cover the header + one or two rows.
      minHeight:       140,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <p style={{ color: '#475569', fontSize: 9, letterSpacing: '0.18em', margin: 0 }}>
            DIVERGENCE LEADERBOARD
          </p>
          <InfoTooltip
            title="Divergence Points"
            body="Divergence measures your Alpha — the value of your signal when it contradicts the market consensus (Price). Winning against the grain rewards you with significantly higher Reputation."
            accent="#7dd3fc"
            direction="below"
            onLearnMore={onOpenManifesto}
          />
        </div>
        <span style={{ color: '#334155', fontSize: 8, letterSpacing: '0.12em' }}>
          DP = Σ UNITS × (1 − PRICE)
        </span>
      </div>

      {rows.length === 0 ? (
        <p style={{ color: '#334155', fontSize: 11, textAlign: 'center', padding: '10px 0', margin: 0 }}>
          No pledges yet — establish the inaugural position.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r, i) => {
            const isMe        = !r.isMock && r.user_id === currentUser;
            const color       = accentFor(r.user_id);
            const barPct      = maxDp > 0 ? (r.dp / maxDp) * 100 : 0;
            const displayName = r.alias ?? shortCitizen(r.user_id);
            const showBadge   = i < 3 && r.tier !== null;
            return (
              <div
                key={r.user_id}
                style={{
                  backgroundColor: isMe ? '#0c1a2e' : '#0f172a',
                  border:          `1px solid ${isMe ? '#1e3a5a' : '#333'}`,
                  borderRadius:    6,
                  padding:         '8px 12px',
                  transition:      'background 0.4s, border-color 0.4s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 14, width: 20 }}>{MEDALS[i] ?? '·'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                      <span title={r.user_id} style={{ color, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}>
                        {displayName}
                      </span>
                      {showBadge && r.tier !== null && <TierBadge tier={r.tier} />}
                      {isMe && <span style={{ color: '#334155', fontSize: 9 }}>YOU</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
                      <span style={{ color: '#64748b', fontSize: 9 }}>
                        {r.pledge_count} pledge{r.pledge_count === 1 ? '' : 's'}
                      </span>
                      <span style={{ color: '#334155', fontSize: 9 }}>·</span>
                      <span style={{ color: '#64748b', fontSize: 9 }}>
                        {r.units_total.toFixed(0)} units
                      </span>
                      <span style={{ color: '#334155', fontSize: 9 }}>·</span>
                      <span style={{ color: '#94a3b8', fontSize: 9, fontWeight: 700 }}>
                        {r.rp.toLocaleString()} RP
                      </span>
                      <span style={{ color: '#334155', fontSize: 9 }}>·</span>
                      <span style={{ color: accuracyColour(r.accuracy), fontSize: 9, fontWeight: 700 }}>
                        {r.accuracy.toFixed(0)}% ACC
                      </span>
                    </div>
                  </div>
                  <span style={{ color, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {r.dp.toFixed(2)}
                    <span style={{ color: '#334155', fontSize: 8, marginLeft: 3 }}>DP</span>
                  </span>
                </div>
                <div style={{ height: 3, backgroundColor: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                  {/* transform: scaleX is compositor-only — it doesn't
                      invalidate layout, so streaming DP updates glide
                      instead of triggering reflow on each tick. */}
                  <div style={{
                    height:          '100%',
                    width:           '100%',
                    transform:       `scaleX(${barPct / 100})`,
                    transformOrigin: 'left center',
                    backgroundColor: color,
                    borderRadius:    2,
                    transition:      'transform 0.6s ease',
                    willChange:      'transform',
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ color: '#1e293b', fontSize: 9, letterSpacing: '0.1em', margin: '12px 0 0', textAlign: 'center' }}>
        Divergence against consensus compounds faster.
      </p>
    </div>
  );
}

// ─── Small pieces ───────────────────────────────────────────────────────

// Accuracy colour ramps: red below 50 % (mean reverter), slate around
// 50 % (noise), emerald above 65 % (genuine edge). Readable at a glance
// on the dense metrics line.
function accuracyColour(pct: number): string {
  if (pct >= 65) return '#86efac';
  if (pct >= 50) return '#94a3b8';
  return '#fca5a5';
}

function TierBadge({ tier }: { tier: IdentityTier }): React.JSX.Element {
  const isSovereign = tier >= IdentityLevel.SOVEREIGN;
  const color  = isSovereign ? '#fcd34d' : '#7dd3fc';
  const glyph  = isSovereign ? '✦' : '◆';
  const label  = isSovereign ? 'SOV'    : 'CIT';
  return (
    <span
      title={isSovereign ? 'Sovereign (Tier 3)' : 'Citizen (Tier 2)'}
      style={{
        display:         'inline-flex',
        alignItems:      'center',
        gap:             2,
        color,
        backgroundColor: isSovereign ? 'rgba(252,211,77,0.10)' : 'rgba(125,211,252,0.10)',
        fontSize:        7,
        letterSpacing:   '0.22em',
        fontWeight:      800,
        padding:         '1px 5px',
        borderRadius:    3,
        border:          `1px solid ${color}`,
        textShadow:      isSovereign ? '0 0 4px rgba(252,211,77,0.35)' : undefined,
      }}
    >
      {glyph} {label}
    </span>
  );
}
